import {useIntl, defineMessages} from 'react-intl'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getPostalCodeRegex, getPostalCodeInputProps} from '../../utils/postal-code-regex'
import {SITE_COUNTRY_MAP} from '../../constants'
import {useBrand} from '../../contexts/brand-context'

const messages = defineMessages({
    orderNumberPlaceholder: {
        defaultMessage: 'Order Number',
        id: 'use_order_status_fields.placeholder.order_number'
    },
    orderEmailPlaceholder: {
        defaultMessage: 'Order Email',
        id: 'use_order_status_fields.placeholder.order_email'
    },
    billingPostalCodePlaceholder: {
        defaultMessage: 'Billing Postal Code',
        id: 'use_order_status_fields.placeholder.billing_postal_code'
    },
    billingZipCodePlaceholder: {
        defaultMessage: 'Billing Zip Code',
        id: 'use_order_status_fields.placeholder.billing_zip_code'
    },
    orderNumberRequiredGarage: {
        defaultMessage: 'Enter your order number',
        id: 'use_order_status_fields.error.order_number_required.garage'
    },
    orderNumberRequiredDynamite: {
        defaultMessage: 'Enter your order number',
        id: 'use_order_status_fields.error.order_number_required.dynamite'
    },
    emailRequiredGarage: {
        defaultMessage: 'Enter the email address associated with the order',
        id: 'use_order_status_fields.error.email_required.garage'
    },
    emailRequiredDynamite: {
        defaultMessage: 'Enter the email address associated with the order',
        id: 'use_order_status_fields.error.email_required.dynamite'
    },
    emailInvalidGarage: {
        defaultMessage: 'Enter a valid email',
        id: 'use_order_status_fields.error.email_invalid.garage'
    },
    emailInvalidDynamite: {
        defaultMessage: 'Enter a valid email',
        id: 'use_order_status_fields.error.email_invalid.dynamite'
    },
    emailMaxLength: {
        defaultMessage: 'Email address must be 50 characters or less',
        id: 'use_order_status_fields.error.email_max_length'
    },
    postcodeRequiredGarage: {
        defaultMessage: 'Enter a valid postcode',
        id: 'use_order_status_fields.error.postcode_required.garage'
    },
    postcodeRequiredDynamite: {
        defaultMessage: 'Enter a valid postcode',
        id: 'use_order_status_fields.error.postcode_required.dynamite'
    },
    postcodeInvalidGarage: {
        defaultMessage: 'Enter a valid postcode',
        id: 'use_order_status_fields.error.postcode_invalid.garage'
    },
    postcodeInvalidDynamite: {
        defaultMessage: 'Enter a valid postcode',
        id: 'use_order_status_fields.error.postcode_invalid.dynamite'
    }
})

/**
 * A React hook that provides the field definitions for an order status lookup form.
 * @param {Object} form - The object returned from `useForm`
 * @param {Object} form.control - The form control object
 * @param {Object} form.formState.errors - An object containing field errors
 * @returns {Object} Field definitions for use in a form
 */
export default function useOrderStatusFields({
    form: {
        watch,
        control,
        formState: {errors}
    }
}) {
    const {formatMessage} = useIntl()
    const {site} = useMultiSite()
    const formCountryCode = watch?.('countryCode')
    const countryCode = formCountryCode || SITE_COUNTRY_MAP[site?.id] || 'US'
    const {isGarage} = useBrand()

    const fields = {
        orderNumber: {
            name: 'orderNumber',
            type: 'text',
            autoComplete: 'off',
            rules: {
                required: isGarage
                    ? formatMessage(messages.orderNumberRequiredGarage)
                    : formatMessage(messages.orderNumberRequiredDynamite)
            },
            error: errors.orderNumber,
            control,
            label: formatMessage(messages.orderNumberPlaceholder),
            inputProps: {
                'aria-label': 'Order Number',
                'aria-required': 'true',
                'aria-invalid': errors.orderNumber ? 'true' : 'false'
            }
        },
        orderEmail: {
            name: 'orderEmail',
            type: 'email',
            autoComplete: 'email',
            rules: {
                required: isGarage
                    ? formatMessage(messages.emailRequiredGarage)
                    : formatMessage(messages.emailRequiredDynamite),
                pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: isGarage
                        ? formatMessage(messages.emailInvalidGarage)
                        : formatMessage(messages.emailInvalidDynamite)
                },
                maxLength: {
                    value: 50,
                    message: formatMessage(messages.emailMaxLength)
                }
            },
            error: errors.orderEmail,
            control,
            label: formatMessage(messages.orderEmailPlaceholder),
            inputProps: {
                'aria-label': 'Order Email',
                'aria-required': 'true',
                'aria-invalid': errors.orderEmail ? 'true' : 'false',
                maxLength: 50
            }
        },
        billingPostcode: {
            name: 'billingPostcode',
            type: 'text',
            autoComplete: 'postal-code',
            rules: {
                required: isGarage
                    ? formatMessage(messages.postcodeRequiredGarage)
                    : formatMessage(messages.postcodeRequiredDynamite),
                validate: (value) => {
                    const regex = getPostalCodeRegex(countryCode)
                    if (regex) {
                        return (
                            regex.test(value) ||
                            (isGarage
                                ? formatMessage(messages.postcodeInvalidGarage)
                                : formatMessage(messages.postcodeInvalidDynamite))
                        )
                    }
                    return true
                }
            },
            error: errors.billingPostcode,
            control,
            label: formatMessage(
                countryCode === 'CA'
                    ? messages.billingPostalCodePlaceholder
                    : messages.billingZipCodePlaceholder
            ),
            inputProps: ({onChange}) => {
                const countryInputProps = getPostalCodeInputProps(countryCode)
                return {
                    ...(countryInputProps ? countryInputProps({onChange}) : {maxLength: 7}),
                    'aria-label': 'Billing Postal Code',
                    'aria-required': 'true',
                    'aria-invalid': errors.billingPostcode ? 'true' : 'false'
                }
            }
        }
    }

    return fields
}
